import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import {
  endOfMonth,
  getDate,
  getMonth,
  getYear,
  isEqual,
  isSameMonth,
  startOfMonth,
} from 'date-fns';

import { environment } from 'src/environments/environment';
import { Announcement } from '@models/announcement';
import { SiteService } from './site.service';
import { AnnouncementDto } from '@interfaces/announcement';
import { map, tap } from 'rxjs/operators';

export interface BulkUpdateResponse {
  identifiers: [];
  raw: [];
}

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private _activities = new BehaviorSubject<Announcement[]>([]);
  public readonly activities = this._activities.asObservable();

  private _isGettingEvents = new BehaviorSubject<boolean>(false);
  public readonly isGettingEvents = this._isGettingEvents.asObservable();

  constructor(
    private http: HttpClient,
    private siteService: SiteService,
  ) {}

  get currentActivities(): Announcement[] {
    return this._activities.getValue();
  }

  public refreshActivities(
    selectedMonth: Date,
    showLoading = true,
  ): Promise<void> {
    const siteId = this.siteService.currentSiteId;

    if (!siteId) {
      this._activities.next([]);
      this._isGettingEvents.next(false);
      return Promise.resolve();
    }

    // Force the loading state to be true even before the API call
    if (showLoading) {
      this._isGettingEvents.next(true);
      // Clear activities first to ensure a fresh state when force refreshing
      this._activities.next([]);
    }

    return this.getMonthsEvents(
      this.siteService.currentSiteId,
      selectedMonth,
      showLoading,
    )
      .then((activities: Announcement[]) => {
        // Ensure we have a valid array
        const validActivities = activities || [];

        // Always emit the new data
        this._activities.next(validActivities);

        // Ensure loading state is set to false after data is loaded
        if (showLoading) {
          this._isGettingEvents.next(false);
        }
      })
      .catch((error) => {
        console.error('Error refreshing activities:', error);
        // Reset loading state on error
        this._isGettingEvents.next(false);
        // Don't clear activities on error, keep existing data
        throw error; // Re-throw to allow calling code to handle
      });
  }

  /**
   * Fetch events for month from API using month/year parameters to avoid timezone issues.
   * @param siteId
   * @param selectedMonth
   * @param showLoading
   */
  public async getMonthsEvents(
    siteId: number,
    selectedMonth: Date,
    showLoading = true,
  ): Promise<Announcement[]> {
    this._isGettingEvents.next(showLoading);

    // Use month/year parameters instead of timestamps to avoid timezone issues
    const year = getYear(selectedMonth);
    const month = getMonth(selectedMonth); // 0-indexed, matches JavaScript and API

    const url = `${environment.apiv3Url}/announcements/site/${siteId}`;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('month', month.toString());
    queryParams = queryParams.append('year', year.toString());
    // "events" have a styleId of 3 in the announcements table.
    queryParams = queryParams.append('searchBy', '["styleId"]');
    queryParams = queryParams.append('search', '3');
    queryParams = queryParams.append('sortField', 'eventStart');
    queryParams = queryParams.append('sortOrder', 'asc');
    try {
      return lastValueFrom(
        this.http
          .get<{ data: AnnouncementDto[]; totalCount: number }>(url, {
            params: queryParams,
          })
          .pipe(
            map(({ data }) => {
              return data.map((announcement) => new Announcement(announcement));
            }),
            tap(() => {
              this._isGettingEvents.next(false);
            }),
          ),
      );
    } catch (err) {
      this._isGettingEvents.next(false);
      console.error(err);
      return;
    }
  }

  /**
   * Fetch events in range from API.
   * Uses timezone-agnostic ISO date strings when possible, falls back to timestamps.
   * @param siteId
   * @param startDate
   * @param endDate
   */
  public getTimeRangeEvents(
    siteId: number,
    startDate: Date,
    endDate: Date,
  ): Promise<Announcement[]> {
    const url = `${environment.apiv3Url}/announcements/site/${siteId}`;
    let queryParams = new HttpParams();

    // Check if this is a full month range and use month parameters if so
    const isFullMonth = this.isFullMonthRange(startDate, endDate);

    if (isFullMonth) {
      // Use month/year parameters to avoid timezone issues
      const year = getYear(startDate);
      const month = getMonth(startDate); // 0-indexed
      queryParams = queryParams
        .append('month', month.toString())
        .append('year', year.toString());
    } else {
      // Pass discrete date parts so the API can build start/end boundaries in the site's timezone
      queryParams = queryParams
        .append('eventStartYear', getYear(startDate).toString())
        .append('eventStartMonth', (getMonth(startDate) + 1).toString())
        .append('eventStartDay', getDate(startDate).toString())
        .append('eventEndYear', getYear(endDate).toString())
        .append('eventEndMonth', (getMonth(endDate) + 1).toString())
        .append('eventEndDay', getDate(endDate).toString());
    }

    return lastValueFrom(
      this.http
        .get<{ data: AnnouncementDto[]; totalCount: number }>(url, {
          params: queryParams,
        })
        .pipe(map(({ data }) => data.map((a) => new Announcement(a)))),
    ).catch((err) => {
      console.error('ERROR', err);
      throw err; // Re-throw the error so it can be caught by the caller
    });
  }

  /**
   * Check if the date range represents a full month
   */
  private isFullMonthRange(startDate: Date, endDate: Date): boolean {
    if (!isSameMonth(startDate, endDate)) {
      return false;
    }

    const startMatches = isEqual(startDate, startOfMonth(startDate));
    const endMatches = isEqual(endDate, endOfMonth(endDate));

    return startMatches && endMatches;
  }

  public copyPreviousMonthsEvents(
    siteId: number,
    startDate: Date,
  ): Promise<BulkUpdateResponse> {
    const url = `${environment.apiv3Url}/announcements/site/${siteId}/generate`;
    try {
      return lastValueFrom(
        this.http.post<BulkUpdateResponse>(url, {
          startDate: startDate.getTime(),
        }),
      );
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  public deleteMonthsEvents(
    siteId: number,
    startDate: Date,
  ): Promise<BulkUpdateResponse> {
    const url = `${environment.apiv3Url}/announcements/site/${siteId}`;
    const queryParams = new HttpParams().append(
      'startDate',
      startDate.getTime().toString(),
    );
    try {
      return lastValueFrom(
        this.http.delete<BulkUpdateResponse>(url, { params: queryParams }),
      );
    } catch (err) {
      console.error('ERROR', err);
      return;
    }
  }

  public resetEvents() {
    this._activities.next([]);
  }
}
